// frontend/src/components/Leave/HospitalisationReconcileModal.js
import React, { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import Modal from '../UI/Modal';
import FormSelect from '../UI/FormSelect';
import {
  usePreviewHospitalisationReconcile,
  useReconcileProvisionalHospitalisation,
} from '../../hooks/useLeaveData';
import { formatDate } from '../../utils/dateUtils';

const ACTION_OPTIONS = [
  { value: 'unpaid', label: 'Confirmed unpaid absence' },
  { value: 'Annual Leave', label: 'Annual Leave' },
  { value: 'Outpatient Sick Leave', label: 'Outpatient Sick Leave' },
  { value: 'Off in Lieu', label: 'Off in Lieu' },
  { value: 'Unpaid Leave', label: 'Unpaid Leave' },
];

const HospitalisationReconcileModal = ({ isOpen, onClose, onSuccess, application }) => {
  const [unsupported, setUnsupported] = useState([]);
  const [coverage, setCoverage] = useState(null);
  const [actions, setActions] = useState({});
  const [payroll, setPayroll] = useState(null);
  const preview = usePreviewHospitalisationReconcile();
  const commit = useReconcileProvisionalHospitalisation();
  const previewMutate = preview.mutate;

  useEffect(() => {
    if (!isOpen || !application?.id) return undefined;
    let cancelled = false;
    previewMutate(
      { applicationId: application.id, data: {} },
      {
        onSuccess: (resp) => {
          if (cancelled) return;
          const data = resp.data || {};
          const nextCoverage = data.coverage || {};
          setCoverage(nextCoverage);
          setUnsupported(nextCoverage.unsupported_dates || []);
          setPayroll(data.payroll_adjustment || null);
          const nextActions = {};
          (nextCoverage.unsupported_dates || []).forEach((iso) => {
            nextActions[iso] = 'unpaid';
          });
          setActions(nextActions);
        },
      },
    );
    return () => {
      cancelled = true;
    };
  }, [isOpen, application?.id, previewMutate]);

  const handleSubmit = () => {
    const mappings = unsupported.map((iso) => {
      const selected = actions[iso] || 'unpaid';
      if (selected === 'unpaid' || selected === 'Unpaid Leave') {
        return { date: iso, action: 'unpaid' };
      }
      return { date: iso, action: 'leave', leave_type_name: selected };
    });
    commit.mutate(
      { applicationId: application.id, data: { mappings } },
      {
        onSuccess: () => {
          onSuccess?.();
          onClose?.();
        },
      },
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Reconcile hospitalisation dates"
      size="md"
      theme="admin"
      contentClassName="space-y-4"
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-admin-outline" disabled={commit.isPending}>
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={commit.isPending || unsupported.length === 0}
            className="btn btn-admin-primary"
          >
            {commit.isPending ? 'Saving...' : 'Commit reconciliation'}
          </button>
        </>
      }
    >
      <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3">
        <div className="flex items-start gap-2 text-sm text-red-800">
          <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p>
            The medical certificate does not cover every provisionally paid day.
            Map each unsupported date. Days are never auto-converted to unpaid.
          </p>
        </div>
      </div>

      {coverage && (
        <p className="text-sm text-gray-600">
          Certificate: {coverage.mc_start || 'missing'} to {coverage.mc_end || 'missing'}.
          Supported {coverage.supported_days || 0} day(s); {unsupported.length} need a decision.
        </p>
      )}

      <div className="space-y-3">
        {unsupported.map((iso) => (
          <div key={iso} className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-center">
            <p className="text-sm font-medium text-gray-900">{formatDate(iso)}</p>
            <FormSelect
              options={ACTION_OPTIONS}
              value={actions[iso] || 'unpaid'}
              onChange={(value) => setActions((prev) => ({ ...prev, [iso]: value }))}
              theme="admin"
            />
          </div>
        ))}
        {unsupported.length === 0 && !preview.isPending && (
          <p className="text-sm text-gray-500">No unsupported dates to map.</p>
        )}
      </div>

      {payroll?.needed && (
        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
          Locked payroll detected for {payroll.dates?.length || 0} day(s).
          A salary overpayment recovery of {payroll.amount} will be added to the next run.
        </p>
      )}
    </Modal>
  );
};

export default HospitalisationReconcileModal;
